import React, { useEffect, useRef, useState } from 'react';
import { t } from 'ttag';
import { VisuallyHidden } from '~/lib/a11y/VisuallyHidden';
import { useTrainingStore } from '~/training/state';

/** 1-based slide index among course sequences (CAT-1433 / EL–E225). */
export const getSlideOrdinal = (
  items: Array<{ kind: string; id: string }>,
  sequenceId: string
): { current: number; total: number } | null => {
  const sequences = items.filter(i => i.kind === 'sequence');
  const index = sequences.findIndex(i => i.id === sequenceId);
  if (index < 0 || sequences.length === 0) return null;
  return { current: index + 1, total: sequences.length };
};

/**
 * After Next / Prev / TOC navigation, announce "Slide N of M" via a stable
 * status live region. Empty on first paint / first resolved slide so the
 * initial load is not noisy (WCAG 4.1.3).
 */
export const SlideChangeAnnouncer = ({
  sequenceId
}: {
  sequenceId: string;
}) => {
  const items = useTrainingStore(s => s.items);
  const [message, setMessage] = useState('');
  const previousSequenceIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!sequenceId) return;

    const ordinal = getSlideOrdinal(items, sequenceId);
    if (!ordinal) return;

    if (previousSequenceIdRef.current === sequenceId) return;

    const previousId = previousSequenceIdRef.current;
    previousSequenceIdRef.current = sequenceId;

    // First resolved slide of this mount — region stays empty.
    if (previousId === null) return;

    setMessage('');
    const timer = window.setTimeout(() => {
      setMessage(t`Slide ${ordinal.current} of ${ordinal.total}`);
    }, 50);

    return () => window.clearTimeout(timer);
  }, [sequenceId, items]);

  return (
    <VisuallyHidden role="status" aria-live="polite" aria-atomic="true">
      {message}
    </VisuallyHidden>
  );
};
